import {
  Megaphone,
  BookOpen,
  Monitor,
  Scale,
  Building2,
  Users,
  Briefcase,
  Store,
  Wallet,
  Code2,
  GraduationCap,
  PartyPopper,
  Plane,
  PenLine,
  ShoppingBag,
  Gavel,
  Calculator,
} from "lucide-react";

export interface Persona {
  id: string;
  title: string;
  tagline: string;
  description: string;
  icon: typeof Megaphone;
  color: string;
  /** Real tool IDs from tools-data.ts -- every one is verified to exist. */
  toolIds: string[];
}

export const personas: Persona[] = [
  {
    id: "marketing",
    title: "Marketing Person",
    tagline:
      "Campaign assets, QR codes, and social content without extra software",
    description:
      "Everything for running campaigns \u2014 from QR codes for print materials to social captions and SEO meta tags \u2014 in one place.",
    icon: Megaphone,
    color: "oklch(0.6 0.2 330)",
    toolIds: [
      "audio-to-subtitles",
      "qr-generator",
      "custom-qr",
      "hashtag-generator",
      "caption-generator",
      "meta-tag-gen",
      "og-preview",
      "document-scanner",
      "voice-recorder",
      "camera-capture",
      "color-palette",
      "gradient-generator",
      "meme-generator",
      "schema-generator",
      "social-media-formatter",
      "banner-sign-maker",
      "product-description-writer",
      "magazine-maker",
      "powerpoint-to-images",
      "images-to-powerpoint",
      "markdown-to-powerpoint",
      "compress-powerpoint",
      "merge-powerpoint",
      "find-replace-powerpoint",
      "replace-fonts-powerpoint",
      "watermark-powerpoint",
      "slide-deck-maker",
      "powerpoint-templates",
      "powerpoint-to-video",
      "presentation-studio",
      "photo-slideshow-maker",
    ],
  },
  {
    id: "content-writer",
    title: "Book & Technical Content Writer",
    tagline: "Proofread, format, and polish long-form writing",
    description:
      "Word counts, grammar checks, and text tools built for people writing something longer than a tweet.",
    icon: BookOpen,
    color: "oklch(0.55 0.15 250)",
    toolIds: [
      "word-counter",
      "grammar-checker",
      "audio-to-subtitles",
      "text-to-speech",
      "voice-recorder",
      "find-replace",
      "text-paraphraser",
      "case-converter",
      "lorem-ipsum",
      "reading-level-checker",
      "text-sorter",
      "markdown-html-converter",
      "markdown-editor",
      "slug-generator",
      "citation-generator",
      "text-similarity-checker",
      "book-writing-toolbox",
    ],
  },
  {
    id: "web-designer",
    title: "Web Designer & Maintenance",
    tagline: "Colors, images, and site assets, ready to use",
    description:
      "Color palettes, favicons, image optimization, and mockups \u2014 the everyday utility work behind building and maintaining a site.",
    icon: Monitor,
    color: "oklch(0.5 0.15 200)",
    toolIds: [
      "color-palette",
      "gradient-generator",
      "favicon-generator",
      "compress-image",
      "resize-crop",
      "convert-image",
      "mockup-generator",
      "logo-maker",
      "image-to-svg",
      "robots-sitemap",
      "contrast-checker",
      "font-pairing-tool",
      "box-shadow-generator",
      "border-radius-generator",
      "clip-path-generator",
      "css-js-minifier",
    ],
  },
  {
    id: "legal",
    title: "Legal Person",
    tagline: "Document templates and official forms in one place",
    description:
      "General-purpose legal templates \u2014 NDAs, leases, powers of attorney \u2014 plus direct links to official government forms.",
    icon: Scale,
    color: "oklch(0.55 0.2 25)",
    toolIds: [
      "nda-generator",
      "lease-agreement-generator",
      "power-of-attorney-generator",
      "bill-of-sale-generator",
      "promissory-note-generator",
      "eviction-notice-generator",
      "contract-templates",
      "government-forms-hub",
      "fillable-forms",
      "pdf-editor",
      "organize-pdf",
      "add-page-numbers",
      "bates-numbering",
      "exhibit-stamper",
      "redact-pdf",
      "compare-pdf",
      "ocr-pdf",
      "redact-powerpoint",
      "password-protect-powerpoint",
      "employment-offer-letter",
      "invention-disclosure-worksheet",
    ],
  },
  {
    id: "real-estate",
    title: "Property & Real Estate Agent",
    tagline: "Lease agreements, listings, and closing paperwork",
    description:
      "The recurring paperwork of buying, selling, and renting property \u2014 lease agreements, bills of sale, and mortgage estimates.",
    icon: Building2,
    color: "oklch(0.6 0.15 100)",
    toolIds: [
      "lease-agreement-generator",
      "rental-application-maker",
      "open-house-sign-in-sheet",
      "bill-of-sale-generator",
      "mortgage-calculator",
      "qr-generator",
      "business-card",
      "camera-capture",
      "invoice-generator",
      "moving-cost-estimator",
      "cost-of-living-comparison",
    ],
  },
  {
    id: "hr",
    title: "HR Person",
    tagline: "Hiring paperwork from offer to onboarding",
    description:
      "From an offer letter to I-9 verification \u2014 the documents that come up every time someone joins the team.",
    icon: Users,
    color: "oklch(0.55 0.18 130)",
    toolIds: [
      "blind-cv-redactor",
      "resume-builder",
      "cover-letter-maker",
      "pay-stub-generator",
      "resume-keyword-matcher",
      "job-experience-calculator",
      "employment-offer-letter",
      "government-forms-hub",
      "fillable-forms",
      "expense-tracker",
      "password-generator",
      "document-scanner",
      "redact-pdf",
      "clean-powerpoint",
      "powerpoint-to-pdf",
      "pdf-to-powerpoint",
      "merge-powerpoint",
      "organize-slides",
    ],
  },
  {
    id: "freelancer",
    title: "Freelancer & Self-Employed",
    tagline:
      "Invoicing, taxes, and contracts \u2014 the parts nobody else handles for you",
    description:
      "Without an employer handling withholding or invoicing for you, these are the tools that come up constantly once you're on your own.",
    icon: Briefcase,
    color: "oklch(0.55 0.2 350)",
    toolIds: [
      "self-employment-tax",
      "income-tax-calculator",
      "invoice-generator",
      "proposal-builder",
      "receipt-maker",
      "expense-tracker",
      "contract-templates",
      "government-forms-hub",
      "fillable-forms",
      "business-card",
      "profit-margin-calculator",
      "break-even-calculator",
    ],
  },
  {
    id: "small-business",
    title: "Small Business Owner",
    tagline: "Invoicing, receipts, and day-to-day paperwork",
    description:
      "The operational basics of running a small business \u2014 invoicing customers, tracking expenses, and staying on top of tax and hiring paperwork.",
    icon: Store,
    color: "oklch(0.6 0.18 145)",
    toolIds: [
      "invoice-generator",
      "proposal-builder",
      "pay-stub-generator",
      "receipt-maker",
      "expense-tracker",
      "business-card",
      "sales-tax-calculator",
      "qr-generator",
      "barcode-generator",
      "document-scanner",
      "contract-templates",
      "profit-margin-calculator",
      "marketplace-fee-calculator",
      "etsy-listing-checker",
      "digital-download-packager",
      "break-even-calculator",
      "inventory-tracker",
      "menu-card-maker",
      "brochure-flyer-maker",
      "product-description-writer",
      "user-manual-writer",
      "catalog-maker",
      "document-assembler",
      "clean-powerpoint",
      "powerpoint-to-pdf",
      "images-to-powerpoint",
      "compress-powerpoint",
      "organize-slides",
      "find-replace-powerpoint",
      "replace-fonts-powerpoint",
      "add-slide-numbers",
      "watermark-powerpoint",
      "resize-slides",
      "accessibility-checker-powerpoint",
      "extract-charts-powerpoint",
      "word-to-powerpoint",
      "excel-to-powerpoint",
      "slide-deck-maker",
      "powerpoint-templates",
      "presentation-studio",
      "redact-powerpoint",
      "password-protect-powerpoint",
    ],
  },
  {
    id: "personal-finance",
    title: "Personal Finance Planner",
    tagline: "Loans, investing, and retirement, modeled out honestly",
    description:
      "For anyone planning their own finances \u2014 not a business \u2014 modeling loans, investments, and retirement timelines with real formulas.",
    icon: Wallet,
    color: "oklch(0.55 0.15 175)",
    toolIds: [
      "bank-statement-to-csv",
      "loan-calculator",
      "mortgage-calculator",
      "compound-interest",
      "sip-calculator",
      "social-security-calculator",
      "401k-calculator",
      "rmd-calculator",
      "fire-calculator",
      "income-tax-calculator",
      "discount-calculator",
      "monthly-budget-planner",
      "college-529-calculator",
    ],
  },
  {
    id: "developer",
    title: "Developer",
    tagline: "The small utilities you reach for a dozen times a day",
    description:
      "JSON formatting, regex testing, hashing, encoding \u2014 the everyday developer utilities that don't deserve their own npm install.",
    icon: Code2,
    color: "oklch(0.5 0.18 265)",
    toolIds: [
      "json-formatter",
      "json-csv-converter",
      "regex-tester",
      "base64",
      "hash-generator",
      "jwt-decoder",
      "uuid-generator",
      "diff-checker",
      "binary-ascii-converter",
      "html-to-pdf",
      "timestamp-converter",
      "cron-builder",
      "http-status-reference",
      "css-js-minifier",
      "user-agent-parser",
      "url-encoder",
      "user-manual-writer",
    ],
  },
  {
    id: "student",
    title: "Student",
    tagline: "College costs, GPA, and everyday budgeting, sorted",
    description:
      "From estimating what school will actually cost to keeping a grocery budget in check \u2014 built for the parts of student life that aren't in the syllabus.",
    icon: GraduationCap,
    color: "oklch(0.55 0.18 265)",
    toolIds: [
      "college-cost-calculator",
      "student-loan-calculator",
      "college-529-calculator",
      "gpa-calculator",
      "voice-recorder",
      "monthly-budget-planner",
      "grocery-list-planner",
      "reading-level-checker",
      "word-counter",
      "percentage-calculator",
      "online-timer",
      "business-days-calculator",
      "citation-generator",
      "paper-format-checker",
      "markdown-to-powerpoint",
      "powerpoint-to-pdf",
      "extract-from-powerpoint",
      "pdf-to-powerpoint",
      "split-powerpoint",
      "organize-slides",
      "compress-powerpoint",
      "find-replace-powerpoint",
      "add-slide-numbers",
      "resize-slides",
      "accessibility-checker-powerpoint",
      "extract-charts-powerpoint",
      "word-to-powerpoint",
      "excel-to-powerpoint",
      "slide-deck-maker",
      "powerpoint-templates",
      "powerpoint-to-video",
      "photo-slideshow-maker",
    ],
  },
  {
    id: "event-planner",
    title: "Event Planner",
    tagline: "Budgets, guest lists, and invitations, all in one place",
    description:
      "The full arc of planning an event \u2014 budget, guest list, day-of schedule, and the invitation itself.",
    icon: PartyPopper,
    color: "oklch(0.62 0.2 350)",
    toolIds: [
      "event-budget-planner",
      "event-checklist-planner",
      "guest-list-tracker",
      "name-badge-maker",
      "certificate-maker",
      "mail-merge-pdf",
      "dietary-access-summary",
      "calendar-invite-maker",
      "event-landing-page-generator",
      "agenda-builder",
      "focus-stage-timer",
      "badge-pass-maker",
      "invite-calendar-maker",
      "expense-capture-sheet",
      "agenda-cue-sheet-builder",
      "rooming-dietary-sheet",
      "vendor-quote-comparison",
      "quote-comparison-builder",
      "guest-manager",
      "invitation-automator",
      "programme-show-day-planner",
      "profit-expense-manager",
      "event-report-generator",
      "rooming-list-planner",
      "seating-chart-planner",
      "arrivals-departures-board",
      "vendor-quote-manager",
      "travel-planner",
      "accommodation-manager",
      "seating-floor-planner",
      "event-tax-summary",
      "booth-manager",
      "marketing-materials-manager",
      "giveaway-branding-manager",
      "event-suite",
      "giveaway-winner-picker",
      "merchandise-order-sheet",
      "visitor-lead-capture-sheet",
      "exhibitor-booth-tracker",
      "booth-layout-planner",
      "event-closure-report-maker",
      "timesheet-generator",
      "receipt-to-expense-sheet",
      "event-profit-ledger",
      "catering-quantity-calculator",
      "run-of-show-cue-sheet",
      "event-day-schedule",
      "invitation-card-maker",
      "menu-card-maker",
      "banner-sign-maker",
    ],
  },
  {
    id: "traveler",
    title: "Traveler",
    tagline: "Budget, pack, and plan a trip without the spreadsheet headache",
    description:
      "Trip budgeting, day-by-day itineraries, and packing lists \u2014 for anyone planning a trip, solo or with a group.",
    icon: Plane,
    color: "oklch(0.6 0.15 200)",
    toolIds: [
      "travel-budget-planner",
      "travel-itinerary-planner",
      "travel-packing-list",
      "travel-countdown-planner",
      "currency-converter",
      "timezone-converter",
    ],
  },
  {
    id: "authors",
    title: "Self-Publishing Author",
    tagline: "From manuscript to print and ebook files",
    description:
      "Format a manuscript, size the cover, check an ISBN, build the interior and the EPUB, and work out print cost and royalty before you upload.",
    icon: PenLine,
    color: "oklch(0.55 0.17 40)",
    toolIds: [
      "manuscript-formatter",
      "book-page-estimator",
      "book-interior-maker",
      "book-spine-calculator",
      "print-royalty-calculator",
      "isbn-checker",
      "copyright-page-maker",
      "epub-maker",
      "epub-checker",
      "kdp-description-formatter",
      "onix-generator",
      "pdf-bookmarks-editor",
      "low-content-interior-maker",
      "acx-audiobook-checker",
    ],
  },
  {
    id: "online-sellers",
    title: "Online Seller",
    tagline: "Listings, photos and digital products ready to publish",
    description:
      "Check listing titles and tags, prepare photos for a marketplace, pack a digital download, print labels and work out fees and margin.",
    icon: ShoppingBag,
    color: "oklch(0.6 0.17 20)",
    toolIds: [
      "seller-studio",
      "etsy-listing-checker",
      "listing-photo-pack",
      "compress-image",
      "remove-background",
      "heic-to-jpg",
      "digital-download-packager",
      "label-sheet-maker",
      "marketplace-fee-calculator",
      "profit-margin-calculator",
      "sales-tax-calculator",
      "barcode-generator",
      "invoice-generator",
    ],
  },
  {
    id: "paralegals",
    title: "Paralegal",
    tagline: "Prepare, number and redact documents for filing",
    description:
      "Stamp Bates numbers and exhibit labels, redact personal details, merge and split PDFs, add page numbers and bookmarks, and mail-merge notices.",
    icon: Gavel,
    color: "oklch(0.5 0.12 260)",
    toolIds: [
      "legal-production-desk",
      "bates-numbering",
      "exhibit-stamper",
      "redact-pdf",
      "merge-pdf",
      "split-pdf",
      "add-page-numbers",
      "pdf-bookmarks-editor",
      "compress-pdf",
      "ocr-pdf",
      "sign-pdf",
      "mail-merge-pdf",
    ],
  },
  {
    id: "bookkeepers",
    title: "Bookkeeper",
    tagline: "Get statements and receipts into a spreadsheet",
    description:
      "Turn bank statements and PDF tables into CSV or Excel, check them against the balances, convert files and keep expense and invoice records.",
    icon: Calculator,
    color: "oklch(0.55 0.13 160)",
    toolIds: [
      "bank-statement-to-csv",
      "pdf-to-excel",
      "csv-converter",
      "json-csv-converter",
      "receipt-to-expense-sheet",
      "expense-tracker",
      "invoice-generator",
      "sales-tax-calculator",
      "ocr-pdf",
      "merge-pdf",
      "redact-pdf",
    ],
  },
];

export function getPersonaById(id: string): Persona | undefined {
  return personas.find(p => p.id === id);
}
